/**
 * @file CategoryEditDialog.tsx
 * @description Create / edit категории каталога (phase08.5 shadcn).
 *
 * phase18: блок «Фото категории» — только в режиме редактирования (нужен id).
 * phase19.16 Р2а (§19.8): фото — из медиатеки с точкой фокуса, квадратная
 * плитка на сайте (`CategoryImageField`); сохраняется сразу.
 *
 * phase19.22 (W22): «Вид товара по умолчанию» (`CategoryKindField`); у
 * системных категорий (`shopId = null`) поля нет. В PATCH вид уходит, только
 * если его поменяли — смена вида пересчитывает партии поддерева на сервере.
 *
 * Р3в (SB131, SEO11): адрес, галочка «Переадресовать со старого адреса» и
 * «Ключевые запросы» — `CategorySlugSeoFields`; тост о снятой переадресации.
 */

import { useEffect, useState } from "react";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { useNotify } from "../ui/NotifyProvider";
import { catalogCategories, type CatalogCategory } from "../../lib/api-catalog";
import { ApiError } from "../../lib/api-client";
import type { ProductKind } from "../../lib/api-product-kinds";
import { keywordsInput, keywordsOf } from "../../lib/site-seo/keywords";
import { notifyRedirectChange } from "../../lib/site-seo/redirect-change";
import { CategoryImageField } from "./CategoryImageField";
import { CategorySlugSeoFields, type CategorySlugSeoValue } from "./CategorySlugSeoFields";
import { CategoryKindField } from "./product-kinds/CategoryKindField";

interface Props {
  open: boolean;
  category: CatalogCategory | null;
  parentId?: string | null;
  /** phase19.22 — справочник видов (включая архивные). */
  kinds: ProductKind[];
  /** Вид от предков: у правки — `inheritedKind`, у новой — действующий вид родителя. */
  inheritedKindName: string | null;
  onClose: () => void;
  onSaved: () => void;
  /** Фото категории сохранилось сразу (Р2а) — перечитать список, окно остаётся открытым. */
  onImageChanged?: () => void;
}

export function CategoryEditDialog({
  open,
  category,
  parentId,
  kinds,
  inheritedKindName,
  onClose,
  onSaved,
  onImageChanged,
}: Props) {
  const notify = useNotify();
  const isEdit = !!category;
  const [displayName, setDisplayName] = useState("");
  const [seo, setSeo] = useState<CategorySlugSeoValue>({ slug: "", redirectFromOldSlug: true, keywords: keywordsOf(null) });
  const slug = seo.slug;
  const [description, setDescription] = useState("");
  const [sortOrder, setSortOrder] = useState<number>(0);
  const [productKindId, setProductKindId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Системной категории вид не задают (CHECK в базе) — поле прячем.
  const kindEditable = !category || category.shopId !== null;
  const [error, setError] = useState<string | null>(null);
  // Р2а — фото категории сохраняется сразу; здесь — свежая категория с сервера.
  const [current, setCurrent] = useState<CatalogCategory | null>(category);

  useEffect(() => {
    if (category) {
      setDisplayName(category.displayName);
      setDescription(category.description ?? "");
      setSortOrder(category.sortOrder);
      setProductKindId(category.productKindId ?? "");
    } else {
      setDisplayName("");
      setDescription("");
      setSortOrder(0);
      setProductKindId("");
    }
    setSeo({ slug: category?.slug ?? "", redirectFromOldSlug: true, keywords: keywordsOf(category) });
    setCurrent(category);
    setError(null);
  }, [category, open]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!displayName.trim()) {
      setError("Название обязательно.");
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit && category) {
        const saved = await catalogCategories.update(category.id, {
          displayName: displayName.trim(),
          slug: slug.trim() || undefined,
          ...(kindEditable ? keywordsInput(seo.keywords) : {}),
          ...(slug.trim() && slug.trim() !== category.slug ? { redirectFromOldSlug: seo.redirectFromOldSlug } : {}),
          description: description.trim() || undefined,
          sortOrder,
          productKindId:
            kindEditable && productKindId !== (category.productKindId ?? "")
              ? productKindId || null
              : undefined,
        });
        notify.success("Категория обновлена");
        notifyRedirectChange(saved.redirect, notify);
      } else {
        await catalogCategories.create({
          parentId: parentId ?? undefined,
          displayName: displayName.trim(),
          slug: slug.trim() || undefined,
          description: description.trim() || undefined,
          sortOrder,
          productKindId: productKindId || undefined,
          ...keywordsInput(seo.keywords),
        });
        notify.success("Категория создана");
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-h-[calc(100dvh-2rem)] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Редактировать категорию" : "Новая категория"}</DialogTitle>
        </DialogHeader>

        <form id="category-form" onSubmit={(e) => void onSubmit(e)} className="-mx-1 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-1">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="c-name">Название *</Label>
            <Input id="c-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} disabled={submitting} required />
          </div>
          <CategorySlugSeoFields
            value={seo}
            onChange={(patch) => setSeo((prev) => ({ ...prev, ...patch }))}
            savedSlug={category?.slug ?? null}
            shopOwned={kindEditable}
            title={displayName}
            conflicts={category?.seoKeywordConflicts ?? []}
            disabled={submitting}
          />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="c-desc">Описание</Label>
            <Textarea id="c-desc" value={description} onChange={(e) => setDescription(e.target.value)} disabled={submitting} rows={2} />
          </div>
          {kindEditable ? (
            <CategoryKindField
              value={productKindId}
              onChange={setProductKindId}
              kinds={kinds}
              inheritedKindName={inheritedKindName}
              disabled={submitting}
            />
          ) : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="c-sort">Порядок сортировки</Label>
            <Input id="c-sort" type="number" min={0} max={10000} value={sortOrder} onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)} disabled={submitting} />
          </div>
          {/* Р2а — фото категории из медиатеки с фокусом (только при редактировании: нужен id). */}
          {current ? (
            <CategoryImageField
              category={current}
              onChanged={(next) => {
                setCurrent(next);
                onImageChanged?.();
              }}
            />
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </form>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Отмена
          </Button>
          <Button type="submit" form="category-form" disabled={submitting}>
            {submitting ? "Сохраняем…" : isEdit ? "Сохранить" : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
